import { Button } from "@/components/button"
import { ShowWhenTrue } from "@/components/conditionals"
import { Dialog, DialogContent } from "@/components/dialog"
import { InnerCard } from "@/components/inner-card"
import { InnerCardWarning } from "@/components/inner-card-warning"
import { Input } from "@/components/input"
import { DEFAULT_SLIPPAGE, MAX_SLIPPAGE, MIN_SLIPPAGE } from "@/domains/common/config/slippage"
import { cn, escapeRegExp } from "@/domains/library"

export function SlippageSettingsModal({
  isOpen,
  slippage,
  title,
  onSlippageChanged,
  onClose,
}: {
  isOpen: boolean
  slippage: string
  title: string
  onSlippageChanged: (slippage: string) => void
  onClose: (isOpen: boolean) => void
}) {
  const handleSlippageChanged = (value: number) => {
    const num = value && !isNaN(value) ? Number(value) : DEFAULT_SLIPPAGE
    onSlippageChanged(String(Math.min(Math.max(num, MIN_SLIPPAGE), MAX_SLIPPAGE)))
  }

  const handleCloseDialog = (value: boolean) => {
    onClose(value)
  }

  const slippageIsOutOfRange = Number(slippage) > MAX_SLIPPAGE || Number(slippage) < MIN_SLIPPAGE

  const handleInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSlippageChanged) return
    const val = e.target.value
    if (val === "" || /^\d*(?:\\[.])?\d*$/.test(escapeRegExp(val))) {
      const split = val.split(".")
      if (split.length > 1 && split[1] !== undefined && split[1].length > 4) {
        return
      }
      if (val.length === 2 && val[0] === "0" && val[1] !== undefined && val[1] !== ".") {
        onSlippageChanged(val[1])
        return
      }
      onSlippageChanged(val)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseDialog}>
      <DialogContent className="max-w-sm sm:max-w-md" title={title}>
        <div>
          <div className="mb-2 text-sm text-t300">Slippage</div>

          <InnerCard hasWarning={slippageIsOutOfRange}>
            <div className={cn("items-center justify-between", "flex flex-row")}>
              <div>
                <Input
                  id={"slippage"}
                  value={slippage}
                  className={cn(
                    "grow",
                    "bg-transparent text-lg leading-6 shadow-none",
                    "rounded-xl rounded-l-none border-0 border-transparent",
                    "ring-offset-shaded ring-0 ring-offset-0 ",
                    "focus-within:border-0 focus-within:ring-0 focus:border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                    "pl-0",
                  )}
                  onChange={handleInputChanged}
                  placeholder={"0.1234"}
                  inputMode="decimal"
                  pattern="[0-9]*"
                />
              </div>
            </div>
            <ShowWhenTrue when={slippageIsOutOfRange}>
              <InnerCardWarning warning={"Invalid slippage"} />
            </ShowWhenTrue>
            <div className="grid grid-cols-5 gap-2">
              {[0.1, 0.5, 1, 3, 5].map((value) => {
                return (
                  <Button
                    key={value.toString()}
                    variant={Number(slippage) === Number(value) ? "primary" : "neutral"}
                    onClick={() => {
                      handleSlippageChanged(value)
                      onClose(false)
                    }}
                    className={cn("h-8")}
                  >
                    {`${value.toFixed(1)}%`}
                  </Button>
                )
              })}
            </div>
          </InnerCard>

          <div className="mt-4 flex justify-end">
            <Button variant={"primary"} onClick={() => handleCloseDialog(false)}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
